import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const TOKEN_KEY = "gbb_token";
const USER_KEY = "gbb_user";

export interface AuthUser {
  id: number;
  email: string;
  role: "admin" | "user";
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    if (key === TOKEN_KEY) return raw as unknown as T;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function persist(key: string, value: unknown | null) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
      return;
    }
    if (typeof value === "string") localStorage.setItem(key, value);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable
  }
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
}

const initialState: AuthState = {
  token: readStored<string>(TOKEN_KEY),
  user: readStored<AuthUser>(USER_KEY),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
      persist(TOKEN_KEY, action.payload);
    },
    setUser(state, action: PayloadAction<AuthUser | null>) {
      state.user = action.payload;
      persist(USER_KEY, action.payload);
    },
    setSession(state, action: PayloadAction<{ token: string; user: AuthUser }>) {
      state.token = action.payload.token;
      state.user = action.payload.user;
      persist(TOKEN_KEY, action.payload.token);
      persist(USER_KEY, action.payload.user);
    },
    logout(state) {
      state.token = null;
      state.user = null;
      persist(TOKEN_KEY, null);
      persist(USER_KEY, null);
    },
  },
});

export const { setToken, setUser, setSession, logout } = authSlice.actions;
export default authSlice.reducer;
